import React from "react";
import {
  Grid,
  Box,
  Typography,
  Skeleton,
  Card,
  CardContent,
} from "@mui/material";

import { Service } from "../../types";
import ServiceCard from "./ServiceCard";

interface ServiceGridProps {
  services: Service[];
  isLoading?: boolean;
  emptyMessage?: string;
}

const ServiceGridSkeleton: React.FC = () => (
  <Grid container spacing={3}>
    {[1, 2, 3, 4, 5, 6].map((i) => (
      <Grid size={{ xs: 12, sm: 6, md: 4, lg: 3 }} key={i}>
        <Card sx={{ height: "100%" }}>
          <CardContent>
            <Box sx={{ display: "flex", gap: 2, mb: 2 }}>
              <Skeleton
                variant="rectangular"
                width={48}
                height={48}
                sx={{ borderRadius: 2 }}
              />
              <Box sx={{ flex: 1 }}>
                <Skeleton variant="text" width="70%" height={28} />
                <Skeleton
                  variant="rectangular"
                  width={80}
                  height={20}
                  sx={{ borderRadius: 1 }}
                />
              </Box>
            </Box>
            <Skeleton variant="text" width="100%" />
            <Skeleton variant="text" width="80%" />
            <Box
              sx={{
                mt: 2,
                pt: 2,
                borderTop: "1px solid",
                borderColor: "divider",
              }}
            >
              <Skeleton variant="text" width="40%" />
              <Skeleton variant="text" width="30%" height={32} />
            </Box>
          </CardContent>
        </Card>
      </Grid>
    ))}
  </Grid>
);

const ServiceGrid: React.FC<ServiceGridProps> = ({
  services,
  isLoading = false,
  emptyMessage = "No services available",
}) => {
  if (isLoading) {
    return <ServiceGridSkeleton />;
  }

  if (services.length === 0) {
    return (
      <Box
        sx={{
          py: 8,
          textAlign: "center",
          bgcolor: "background.paper",
          borderRadius: 2,
          border: "1px solid",
          borderColor: "divider",
        }}
      >
        <Typography
          variant="h6"
          sx={{
            color: "text.secondary",
          }}
        >
          {emptyMessage}
        </Typography>
      </Box>
    );
  }

  return (
    <Grid container spacing={3}>
      {services.map((service) => (
        <Grid size={{ xs: 12, sm: 6, md: 4, lg: 3 }} key={service.id}>
          <ServiceCard service={service} />
        </Grid>
      ))}
    </Grid>
  );
};

export default ServiceGrid;
